import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import { LeaseDatePicker, useLinkedLeaseDateRange } from '@/features/LeaseDetails';

type LeaseEndDatePickerProps = {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
  watch: UseFormWatch<Renter>;
  endDateManuallyEdited: React.MutableRefObject<boolean>;
};

export const LeaseEndDatePicker = ({
  control,
  errors,
  setValue,
  trigger,
  watch,
  endDateManuallyEdited,
}: LeaseEndDatePickerProps) => {
  const error = errors.leaseEndDate;
  const leaseStartDate = watch('leaseStartDate');

  const { onEndDateChange, onEndDateError } = useLinkedLeaseDateRange({
    setStartDate: (date, options) =>
      setValue(
        'leaseStartDate',
        date ? (date.toDate() as Date) : (null as unknown as Date),
        options,
      ),
    setEndDate: (date, options) =>
      setValue('leaseEndDate', date ? (date.toDate() as Date) : (null as unknown as Date), options),
    triggerStartDate: () => trigger('leaseStartDate'),
    triggerEndDate: () => trigger('leaseEndDate'),
    endDateManuallyEdited,
  });

  return (
    <Controller
      name="leaseEndDate"
      control={control}
      render={({ field }) => {
        const pickerValue = field.value ? dayjs(field.value) : null;

        return (
          <LeaseDatePicker
            title="Lease end date"
            value={pickerValue}
            onChange={onEndDateChange}
            onError={onEndDateError}
            error={error}
            required
            dataTestId="lease_end"
            minDate={leaseStartDate ? dayjs(leaseStartDate).add(1, 'day') : undefined}
            maxDate={
              leaseStartDate ? dayjs(leaseStartDate).add(10, 'year').add(1, 'month') : undefined
            }
            size="small"
            redAsterisk
          />
        );
      }}
    />
  );
};
